import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Icon, Loader } from 'semantic-ui-react';

import { useGetChatSessionsQuery } from './chatSessionApiSlice';
import { canEvaluate } from '../../constants';
import { scoreColor } from './ChatSessionsList';

/**
 * 대화 내역 on phones. The desktop table squeezed into cards put the score out of sight and
 * the first card half a screen down; this is a list built for a phone instead:
 * compact cards (customer, when/how long, score), at most the actions that apply, a slim
 * search + filter header, and infinite scroll.
 *
 * Infinite scroll keeps its place: the number of loaded pages lives in the URL (`loaded`,
 * replaced in place) and the scroll offset in sessionStorage, so opening a session and
 * pressing back lands where you were. Each page is its own cached request.
 */

const PAGE_SIZE = 15;
const KINDS = [['전체', null], ['평가', 'false'], ['연습', 'true']];
const SCROLL_KEY = 'chatsessionslist:scroll';

const customerLabel = p => (!p ? '실제 고객' : p.int_id ? `${p.int_id}번 고객` : p.name);

const shortDate = iso => {
  const d = new Date(iso);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getMonth() + 1}/${d.getDate()} (${'일월화수목금토'[d.getDay()]}) ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// DRF durations look like "00:12:34.5". Sessions are capped well under a day.
const minutesLabel = duration => {
  const m = duration && duration.match(/(\d+):(\d+):(\d+)/);
  if (!m) return null;
  const mins = Number(m[1]) * 60 + Number(m[2]);
  return mins < 1 ? '1분 미만' : `${mins}분`;
};

const smallButton = primary => ({
  border: 'none',
  borderRadius: '999px',
  padding: '7px 14px',
  fontSize: '13px',
  fontWeight: 600,
  backgroundColor: primary ? '#007AFF' : '#EAF3FF',
  color: primary ? 'white' : '#007AFF',
  cursor: 'pointer',
});

const SessionCard = ({ s, isAdmin, onOpen, onResume, onEvaluate }) => {
  const evaluated = s.evaluation_session_count > 0;
  const showResume = !evaluated;
  // canEvaluate already requires 6+ lines and no evaluation yet.
  const showEvaluate = !s.is_practice && canEvaluate(s);
  const meta = [shortDate(s.created_at), minutesLabel(s.duration), `발화 ${s.chatlog_count}개`]
    .filter(Boolean)
    .join(' · ');
  const stop = fn => e => {
    e.stopPropagation();
    fn(s);
  };

  return (
    <div
      role={evaluated ? 'button' : undefined}
      onClick={evaluated ? () => onOpen(s) : undefined}
      style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '14px 16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        border: '1px solid rgba(0,0,0,0.05)',
        cursor: evaluated ? 'pointer' : 'default',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div style={{ flex: 1, minWidth: 0, fontSize: '16px', fontWeight: 700, color: '#1d1d1f', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {customerLabel(s.persona)}
          {s.persona?.int_id && s.persona?.name && (
            <span style={{ fontWeight: 400, color: '#86868b' }}> · {s.persona.name}</span>
          )}
        </div>
        <span
          style={{
            flexShrink: 0,
            fontSize: '12px',
            fontWeight: 600,
            padding: '2px 8px',
            borderRadius: '6px',
            color: s.is_practice ? '#6e6e73' : '#007AFF',
            backgroundColor: s.is_practice ? '#F2F2F7' : '#EAF3FF',
          }}
        >
          {s.is_practice ? '연습' : '평가'}
        </span>
        {evaluated && <Icon name="chevron right" style={{ margin: 0, color: '#c7c7cc', fontSize: '12px' }} />}
      </div>

      <div style={{ marginTop: '4px', fontSize: '13px', color: '#86868b' }}>{meta}</div>

      {isAdmin && (
        <div style={{ marginTop: '2px', fontSize: '13px', color: '#424245' }}>
          {s.trainee?.full_name || s.trainee?.username}
          {s.trainee?.contractor?.name && (
            <span style={{ color: '#86868b' }}> · {s.trainee.contractor.name}</span>
          )}
        </div>
      )}

      {evaluated ? (
        <div style={{ marginTop: '8px', fontSize: '14px', color: '#424245' }}>
          응대 스킬{' '}
          <strong style={{ color: s.latest_evaluation_score != null ? scoreColor(s.latest_evaluation_score) : '#86868b' }}>
            {s.latest_evaluation_score != null ? `${Math.round(s.latest_evaluation_score)}점` : '채점 중'}
          </strong>
          {s.latest_pk_evaluation_score != null && (
            <>
              {' · '}제품 지식{' '}
              <strong style={{ color: scoreColor(s.latest_pk_evaluation_score) }}>
                {Math.round(s.latest_pk_evaluation_score)}점
              </strong>
            </>
          )}
        </div>
      ) : (
        !s.is_practice && (
          <div style={{ marginTop: '8px', fontSize: '13px', color: '#86868b' }}>평가 전</div>
        )
      )}

      {(showResume || showEvaluate) && (
        <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
          {showResume && (
            <button type="button" style={smallButton(!showEvaluate)} onClick={stop(onResume)}>
              <Icon name="comments" style={{ marginRight: '4px' }} />
              이어하기
            </button>
          )}
          {showEvaluate && (
            <button type="button" style={smallButton(true)} onClick={stop(onEvaluate)}>
              <Icon name="clipboard check" style={{ marginRight: '4px' }} />
              평가 실행
            </button>
          )}
        </div>
      )}
    </div>
  );
};

/** One page of results; its own cached request, so restoring N pages is N cache hits. */
const Chunk = ({ page, params, onLoaded, renderCard }) => {
  const { data, isError } = useGetChatSessionsQuery(
    { ...params, page },
    { refetchOnMountOrArgChange: 60 },
  );
  useEffect(() => {
    if (data) onLoaded(page, !!data.next, data.count);
  }, [data, page, onLoaded]);
  if (isError) {
    return <div style={{ textAlign: 'center', color: '#C0392B', padding: '12px' }}>불러오지 못했습니다.</div>;
  }
  if (!data) return <Loader active inline="centered" size="small" style={{ margin: '16px auto' }} />;
  return data.results.map(renderCard);
};

const MobileChatSessionList = ({ isAdmin, onOpen, onEvaluate, onUpload }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const qs = new URLSearchParams(location.search);
  const search = qs.get('search') || '';
  const startDate = qs.get('start_date');
  const endDate = qs.get('end_date');
  const kind = qs.get('is_practice');
  const loaded = Math.max(1, parseInt(qs.get('loaded') || '1', 10) || 1);

  const [searchValue, setSearchValue] = useState(search);
  const [filtersOpen, setFiltersOpen] = useState(!!(startDate || endDate));
  const [tail, setTail] = useState({ page: 0, hasNext: false, count: null });

  const params = Object.fromEntries(
    Object.entries({
      search,
      start_date: startDate,
      end_date: endDate,
      is_practice: kind,
      has_lines: 1,
      page_size: PAGE_SIZE,
    }).filter(([, v]) => v),
  );
  const filterKey = JSON.stringify(params);

  // keepPlace: only `loaded` changes (infinite scroll) — replace, don't add history.
  const setQuery = (changes, keepPlace = false) => {
    const p = new URLSearchParams(location.search);
    Object.entries(changes).forEach(([k, v]) => (v ? p.set(k, v) : p.delete(k)));
    if (!keepPlace) {
      p.delete('loaded');
      p.delete('page');
      window.scrollTo(0, 0);
    }
    navigate(`${location.pathname}?${p.toString()}`, { replace: keepPlace });
  };

  const onLoaded = useCallback(
    (page, hasNext, count) => {
      if (page === loaded) setTail({ page, hasNext, count });
    },
    [loaded],
  );

  // Back from a session: restore the scroll offset once every loaded page is in.
  const restored = useRef(false);
  useEffect(() => {
    if (restored.current || tail.page !== loaded) return;
    restored.current = true;
    try {
      const saved = JSON.parse(sessionStorage.getItem(SCROLL_KEY) || 'null');
      if (saved && saved.key === location.search) window.scrollTo(0, saved.y);
      sessionStorage.removeItem(SCROLL_KEY);
    } catch (e) { /* private mode: start at the top */ }
  }, [tail.page, loaded, location.search]);

  const rememberPlace = () => {
    try {
      sessionStorage.setItem(SCROLL_KEY, JSON.stringify({ key: location.search, y: window.scrollY }));
    } catch (e) { /* private mode */ }
  };

  const canLoadMore = tail.page === loaded && tail.hasNext;
  const loadMore = useCallback(() => {
    if (canLoadMore) setQuery({ loaded: String(loaded + 1) }, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canLoadMore, loaded, location.search]);

  const sentinel = useRef(null);
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !canLoadMore) return undefined;
    const io = new IntersectionObserver(([entry]) => entry.isIntersecting && loadMore(), {
      rootMargin: '400px',
    });
    io.observe(el);
    return () => io.disconnect();
  }, [canLoadMore, loadMore]);

  const renderCard = s => (
    <SessionCard
      key={s.id}
      s={s}
      isAdmin={isAdmin}
      onOpen={x => {
        rememberPlace();
        onOpen(x);
      }}
      onResume={x => navigate(`/audio-orb-openai?session_id=${x.id}`)}
      onEvaluate={onEvaluate}
    />
  );

  const hasDates = !!(startDate || endDate);

  return (
    <div style={{ padding: '12px 16px 40px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        <div style={{ flex: 1, fontSize: '20px', fontWeight: 700, color: '#1d1d1f' }}>
          대화 내역
          {tail.count != null && (
            <span style={{ marginLeft: '8px', fontSize: '14px', fontWeight: 400, color: '#86868b' }}>
              총 {tail.count}개
            </span>
          )}
        </div>
        {/* Admin only: uploading a real store recording for evaluation. */}
        {isAdmin && (
          <button type="button" aria-label="녹음 파일 업로드" onClick={onUpload} style={{ ...smallButton(false), padding: '8px 12px' }}>
            <Icon name="upload" style={{ margin: 0 }} />
          </button>
        )}
      </div>

      <div style={{ display: 'flex', gap: '8px' }}>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: '#F2F2F7', borderRadius: '12px', padding: '0 12px', height: '40px' }}>
          <Icon name="search" style={{ margin: 0, color: '#86868b' }} />
          <input
            type="search"
            enterKeyHint="search"
            placeholder="고객, 직원, 대화 내용 검색"
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && setQuery({ search: searchValue.trim() })}
            style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', fontSize: '16px' }}
          />
        </div>
        <button
          type="button"
          aria-label="기간 필터"
          onClick={() => setFiltersOpen(o => !o)}
          style={{ position: 'relative', flexShrink: 0, border: 'none', borderRadius: '12px', width: '40px', height: '40px', backgroundColor: filtersOpen || hasDates ? '#EAF3FF' : '#F2F2F7', color: hasDates ? '#007AFF' : '#424245' }}
        >
          <Icon name="calendar alternate outline" style={{ margin: 0 }} />
        </button>
      </div>

      {filtersOpen && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px', fontSize: '14px', color: '#424245' }}>
          <input type="date" aria-label="시작일" value={startDate || ''} onChange={e => setQuery({ start_date: e.target.value })} style={{ flex: 1, minWidth: 0, height: '36px', border: '1px solid #d2d2d7', borderRadius: '10px', padding: '0 8px', fontSize: '14px' }} />
          <span>~</span>
          <input type="date" aria-label="종료일" value={endDate || ''} onChange={e => setQuery({ end_date: e.target.value })} style={{ flex: 1, minWidth: 0, height: '36px', border: '1px solid #d2d2d7', borderRadius: '10px', padding: '0 8px', fontSize: '14px' }} />
          {hasDates && (
            <button type="button" onClick={() => setQuery({ start_date: null, end_date: null })} style={{ border: 'none', background: 'none', color: '#007AFF', fontSize: '14px', whiteSpace: 'nowrap' }}>
              초기화
            </button>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: '8px', margin: '12px 0' }}>
        {KINDS.map(([label, value]) => {
          const active = (kind || null) === value;
          return (
            <button
              key={label}
              type="button"
              onClick={() => setQuery({ is_practice: value })}
              style={{ border: 'none', borderRadius: '999px', padding: '6px 14px', fontSize: '14px', fontWeight: 600, backgroundColor: active ? '#1d1d1f' : '#F2F2F7', color: active ? 'white' : '#424245' }}
            >
              {label}
            </button>
          );
        })}
      </div>

      <div style={{ display: 'grid', gap: '10px' }}>
        {Array.from({ length: loaded }, (_, i) => (
          <Chunk key={`${filterKey}:${i + 1}`} page={i + 1} params={params} onLoaded={onLoaded} renderCard={renderCard} />
        ))}
      </div>

      {canLoadMore && (
        <div ref={sentinel} style={{ textAlign: 'center', marginTop: '16px' }}>
          <button type="button" onClick={loadMore} style={smallButton(false)}>더 보기</button>
        </div>
      )}
      {tail.page === loaded && !tail.hasNext && (
        <div style={{ textAlign: 'center', marginTop: '20px', fontSize: '13px', color: '#86868b' }}>
          {tail.count ? '모든 대화를 불러왔습니다.' : '대화 기록이 없습니다.'}
        </div>
      )}
    </div>
  );
};

export default MobileChatSessionList;
